import {
  useInfiniteQuery,
  useQuery,
  UseQueryResult,
} from "@tanstack/react-query";
import { useCallback } from "react";
import { useApi } from "@/core/api";
import { CUSTOMER_STATE } from "@/customer/constants";
import type {
  ICustomer,
  CustomerWithPayouts,
  CustomerWithReferralsAndPayouts,
} from "@/customer/customer";
import {
  customerKey,
  customersKey,
  customersV2Key,
} from "@/customer/query/keys";
import { getEnrichedCustomer } from "@/customer/utils";
import { useGetReferrals } from "@/referrals/query";

export interface CustomersV2Counts {
  all: number;
  leads: number;
  advocates: number;
}

export interface CustomersV2Response<TItem = ICustomer> {
  items: TItem[];
  counts: CustomersV2Counts;
  has_more: boolean;
  next_page: number | null;
}

interface UseGetCustomersV2Config {
  customerType?: "all" | "leads" | "advocates";
  enabled?: boolean;
  pageSize?: number;
  search?: string;
  sortBy?: "created_at" | "name" | "referrals_count" | "payouts_total";
  sortDirection?: "asc" | "desc";
  withPayouts?: boolean;
  withReferrals?: boolean;
}

export const useGetCustomer = (customerId?: string | null) => {
  const { get } = useApi();

  return useQuery({
    queryKey: customerKey(customerId),
    queryFn: () =>
      get(`/customers/${customerId}`).then(
        (res) => getEnrichedCustomer(res.data) as ICustomer,
      ),
    enabled: !!customerId,
  });
};

export const useGetAdvocate = (
  advocateId?: string | null,
): UseQueryResult<CustomerWithReferralsAndPayouts> => {
  const { get } = useApi();

  return useQuery({
    queryKey: ["advocate", advocateId] as const,
    queryFn: () =>
      get(`/customers/${advocateId}`).then(
        (res) =>
          getEnrichedCustomer(res.data) as CustomerWithReferralsAndPayouts,
      ),
    enabled: !!advocateId,
  });
};

interface useGetCustomersConfig {
  enabled?: boolean;
  withPayouts?: boolean;
}

export const useGetCustomers = <TData = ICustomer[]>(
  businessId?: string | null,
  select?: (data: TData) => TData,
  { enabled = true, withPayouts = false }: useGetCustomersConfig = {},
) => {
  const api = useApi();

  return useQuery({
    queryKey: customersKey(businessId, withPayouts),
    queryFn: async () => {
      const params = new URLSearchParams({
        business_id: businessId || "",
        exclude_states: CUSTOMER_STATE.opted_out,
      });
      if (withPayouts) {
        params.append("with_payouts", "true");
      }
      return api
        .get("/customers", { params })
        .then((res) => res.data.map(getEnrichedCustomer));
    },
    enabled: !!businessId && enabled,
    select,
  });
};

export const useGetCustomersV2 = <TItem extends ICustomer = ICustomer>(
  businessId?: string | null,
  {
    customerType = "all",
    enabled = true,
    pageSize = 50,
    search = "",
    sortBy = "created_at",
    sortDirection = "desc",
    withPayouts = false,
    withReferrals = false,
  }: UseGetCustomersV2Config = {},
) => {
  const api = useApi();
  const trimmedSearch = search.trim();

  return useInfiniteQuery({
    queryKey: customersV2Key(businessId, {
      customerType,
      sortBy,
      sortDirection,
      search: trimmedSearch,
      pageSize,
      withPayouts,
      withReferrals,
    }),
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({
        business_id: businessId || "",
        customer_type: customerType,
        exclude_states: CUSTOMER_STATE.opted_out,
        page: String(pageParam),
        page_size: String(pageSize),
        sort_by: sortBy,
        sort_direction: sortDirection,
      });

      if (trimmedSearch) {
        params.set("search", trimmedSearch);
      }

      if (withPayouts) {
        params.set("with_payouts", "true");
      }

      if (withReferrals) {
        params.set("with_referrals", "true");
      }

      return api
        .get("/v2/customers", { params })
        .then((res) => res.data as CustomersV2Response<TItem>)
        .then((data) => ({
          ...data,
          items: data.items.map(
            (customer) => getEnrichedCustomer(customer) as TItem,
          ),
        }));
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.next_page : undefined,
    enabled: !!businessId && enabled,
  });
};

export const useGetCustomersWithReferralsAndPayouts = (
  businessId?: string | null,
): UseQueryResult<CustomerWithReferralsAndPayouts[]> => {
  const {
    data: referralsData,
    isSuccess: isReferralsSuccess,
    isLoading: isReferralsLoading,
  } = useGetReferrals(businessId);

  const customersQuery = useGetCustomers(
    businessId,
    useCallback(
      (customers: CustomerWithPayouts[]): CustomerWithReferralsAndPayouts[] =>
        customers.map((customer) => ({
          ...customer,
          referrals:
            referralsData?.filter(
              (referral) => referral.advocate?.id === customer.id,
            ) || [],
          lead_referral: referralsData?.find(
            (referral) => referral.potential_customer?.id === customer.id,
          ),
        })),
      [referralsData],
    ),
    { enabled: isReferralsSuccess, withPayouts: true },
  );

  return {
    ...customersQuery,
    isLoading: customersQuery.isLoading || isReferralsLoading,
  } as UseQueryResult<CustomerWithReferralsAndPayouts[]>;
};
